import { useMemo } from 'react';
import {
  formatDate,
  formatDateTime,
  ACTIVITY_TYPE_MAP,
  STORE_SHORT_NAMES,
  getDayOfWeek,
  HOSTESS_VISIBLE_CUSTOMER_CATEGORY_EMOJI,
  HOSTESS_VISIBLE_CUSTOMER_CATEGORY_LABEL,
  type HostessVisibleCustomerCategory,
} from '@sakura-grp/lib';
import { calculateVisitStatus, ACTIVITY_THRESHOLDS } from '@sakura-grp/database/services';
import { getStoreLogoUrl, getHostessProfileImageUrlOrPlaceholder } from '@/lib/storage-utils';
import { normalizePostalCodeInput } from '@/lib/api/business-cards';
import { useCustomerDetailRpc, type CustomerDetailRpcData } from './useCustomerDetailRpc';
import { useCustomerActivities, type CustomerActivitiesRpcData } from './useCustomerActivities';
import type {
  CustomerDetailData,
  StoreInfo,
  Announcement,
  Reservation,
  BusinessCard,
  BranchCustomer,
  ActivityType,
  ReservationType,
} from '~/types/customer-detail.types';

/**
 * 顧客詳細を取得する統合 hook
 * RPC 1 (基本データ) + RPC 2 (活動データ) を統合して CustomerDetailData を返す。
 * 顧客カテゴリは RPC 戻り値 visible_customer_category を信頼する。
 */
export function useCustomerDetail(id: string | undefined) {
  const detail = useCustomerDetailRpc(id);
  const activities = useCustomerActivities(id);

  const data = useMemo<CustomerDetailData | undefined>(() => {
    if (!detail.data) return undefined;
    return transformToCustomerDetailData(detail.data, activities.data);
  }, [detail.data, activities.data]);

  return {
    data,
    isLoading: detail.isLoading || activities.isLoading,
    error: detail.error || activities.error,
    refetch: async () => {
      await Promise.all([detail.refetch(), activities.refetch()]);
    },
  };
}

export function transformToCustomerDetailData(
  rpc: CustomerDetailRpcData,
  activitiesRpc: CustomerActivitiesRpcData | undefined,
): CustomerDetailData {
  const c = rpc.customer;

  // --- stores 集約 ---
  const storeMap = new Map<string, StoreInfo>();

  // 1. assigned_hostesses → store + hostesses
  for (const ah of rpc.assigned_hostesses) {
    if (!ah.store_id) continue;
    if (!storeMap.has(ah.store_id)) {
      storeMap.set(ah.store_id, {
        storeId: ah.store_id,
        storeName: ah.store_name || '不明',
        storeLogo: getStoreLogoUrl(ah.store_logo_path),
        storeShortName: STORE_SHORT_NAMES[ah.store_id] || '店',
        hostesses: [],
        bottles: [],
        parents: [],
      });
    }
    storeMap.get(ah.store_id)!.hostesses.push({
      id: ah.hostess_id,
      name: ah.name || '',
      avatar: getHostessProfileImageUrlOrPlaceholder(ah.profile_image_path),
      status: ah.status === 'active' ? 'working' : 'absent',
    });
  }

  // 2. store_relations
  for (const sr of rpc.store_relations) {
    if (!sr.store_id || storeMap.has(sr.store_id)) continue;
    storeMap.set(sr.store_id, {
      storeId: sr.store_id,
      storeName: sr.store_name || '不明',
      storeLogo: getStoreLogoUrl(sr.store_logo_path),
      storeShortName: STORE_SHORT_NAMES[sr.store_id] || '店',
      hostesses: [],
      bottles: [],
      parents: [],
    });
  }

  // 3. keep_bottles → store + bottles
  for (const kb of rpc.keep_bottles) {
    if (!kb.store_id) continue;
    if (!storeMap.has(kb.store_id)) {
      storeMap.set(kb.store_id, {
        storeId: kb.store_id,
        storeName: kb.store_name || '不明',
        storeLogo: getStoreLogoUrl(kb.store_logo_path),
        storeShortName: STORE_SHORT_NAMES[kb.store_id] || '店',
        hostesses: [],
        bottles: [],
        parents: [],
      });
    }
    storeMap.get(kb.store_id)!.bottles.push({
      id: kb.id,
      name: kb.bottle_name || '不明',
    });
  }

  // 4. parent_referrals → existing store's parents
  for (const pr of rpc.parent_referrals) {
    if (!pr.store_id || !storeMap.has(pr.store_id)) continue;
    storeMap.get(pr.store_id)!.parents.push({
      customerId: pr.referrer_id || '',
      customerName: pr.referrer_name || '不明',
      hostessId: '',
      hostessName: '',
      hostessEmoji: '💁‍♀️',
    });
  }

  // 5. activities → store (from RPC 2)
  if (activitiesRpc) {
    for (const a of activitiesRpc.activities) {
      if (!a.store_id || storeMap.has(a.store_id)) continue;
      storeMap.set(a.store_id, {
        storeId: a.store_id,
        storeName: a.store_name || '不明',
        storeLogo: getStoreLogoUrl(a.store_logo_path),
        storeShortName: STORE_SHORT_NAMES[a.store_id] || '店',
        hostesses: [],
        bottles: [],
        parents: [],
      });
    }
  }

  // --- announcements 変換 ---
  const announcements: Announcement[] = (activitiesRpc?.activities || []).map((a) => ({
    id: a.id,
    date: formatDateTime(a.activity_date),
    activityDateIso: a.activity_date,
    store: a.store_name || '不明',
    type: (ACTIVITY_TYPE_MAP[a.activity_type as keyof typeof ACTIVITY_TYPE_MAP] || 'メッセ') as ActivityType,
    hostess: {
      id: a.hostess_id || '',
      name: a.hostess_name || '不明',
      avatar: getHostessProfileImageUrlOrPlaceholder(a.hostess_profile_image_path),
      status: a.hostess_status === 'active' ? 'working' as const : 'absent' as const,
    },
    isMyActivity: a.is_my_activity,
    isRelatedToMe: a.is_my_activity || rpc.is_unassigned_branch,
  }));

  // --- reservations 変換 ---
  const reservations: Reservation[] = (rpc.bookings || []).map((b) => {
    const bookingDate = new Date(b.booking_date);
    const isPast = bookingDate < new Date();
    let reservationType: ReservationType = '予約';
    if (b.has_hostess) reservationType = '同伴';
    else if (b.status === 'confirmed') reservationType = '確約';

    return {
      id: b.id,
      date: formatDate(b.booking_date),
      time: bookingDate.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false }),
      dayOfWeek: getDayOfWeek(b.booking_date),
      store: b.store_name || '不明',
      type: reservationType,
      guestCount: String(b.reserved_guest_count || '') || '-',
      isPast,
    };
  });

  // --- business cards 変換 (signed URLs are added later by the component) ---
  const businessCards: BusinessCard[] = (rpc.business_cards || []).map((bc) => ({
    id: String(bc.id),
    name: bc.name_kanji || '',
    kana: bc.name_furigana || '',
    romaji: '',
    mobile: bc.mobile_number || '',
    email: bc.email_address || '',
    companyCategory: '',
    companyName: bc.company_name || '',
    position: bc.position || '',
    companyPhone: bc.company_phone || '',
    companyFax: bc.fax || '',
    companyAddress: bc.address || '',
    // 郵便番号は保存規約が NNN-NNNN だが、Sansan 由来等の 7 桁ハイフンなしも
    // 表示時に整形する（normalizePostalCodeInput は 7 桁なら NNN-NNNN 化、それ以外は素通し）
    postalCode: bc.postal_code ? normalizePostalCodeInput(bc.postal_code) : '',
    front: bc.file || undefined,
    back: bc.file_back || undefined,
    handwrittenMemo: bc.handwritten_note || '',
  }));

  // --- branches 変換 ---
  const branches: BranchCustomer[] = (rpc.branches || []).map((br) => {
    const total = br.phone_count + br.message_count + br.letter_count + br.visit_count;
    return {
      id: br.id,
      name: br.name || '',
      kana: br.name_kana || '',
      company: br.company_name || '',
      mobilePhone: br.mobile_phone || '',
      storeIds: br.store_ids || [],
      points: 0,
      latestVisit: '',
      salesStatus: ACTIVITY_THRESHOLDS.evaluate(total),
      activityCounts: {
        phone: br.phone_count,
        message: br.message_count,
        letter: br.letter_count,
        visit: br.visit_count,
      },
      restrictions: {
        phone: br.allow_phone === false,
        message: br.allow_message === false,
        dm: br.allow_dm === false,
        visit: br.allow_visit === false,
      },
    };
  });

  // --- activityCounts + salesStatus (from RPC 2) ---
  const activityCounts = activitiesRpc?.counts || { phone: 0, message: 0, letter: 0, visit: 0 };
  const salesStatus = activitiesRpc?.sales_status || 'poor';

  // --- ホステス視点の表示カテゴリ (RPC が算出) ---
  const visibleCategory: HostessVisibleCustomerCategory = rpc.visible_customer_category ?? 'others';
  const icon = HOSTESS_VISIBLE_CUSTOMER_CATEGORY_EMOJI[visibleCategory]
    ?? HOSTESS_VISIBLE_CUSTOMER_CATEGORY_EMOJI.others;
  const label = HOSTESS_VISIBLE_CUSTOMER_CATEGORY_LABEL[visibleCategory];

  return {
    id: c.id,
    type: visibleCategory,
    name: c.name || '',
    kana: c.name_kana || '',
    romaji: '',
    mobilePhone: c.mobile_phone || undefined,
    email: c.email || undefined,
    icon,
    label,
    salesStatus,
    restrictions: {
      phone: c.allow_phone === false,
      message: c.allow_message === false,
      dm: c.allow_dm === false,
      visit: c.allow_visit === false,
    },
    stats: rpc.booking_stats || { dohan: 0, kakuyaku: 0, yoyaku: 0 },
    visitStatus: calculateVisitStatus(rpc.last_visit_date),
    activityCounts,
    stores: Array.from(storeMap.values()),
    announcements,
    reservations,
    businessCards,
    memos: {
      hallMemo: c.memo || '',
      hostessMemo: rpc.hostess_memo || '',
    },
    branches: visibleCategory === 'assigned' ? branches : undefined,
  };
}
